import { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Modal,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";

import { isEmpty, validEmail } from "../../utils";
import { showToast } from "../../services/toast";
import { colors } from "../../theme/colors";
import { httpsCallable } from "firebase/functions";
import { auth, db, functions } from "../../services/firebase";
import { FingerprintsType, UserType } from "../../types";
import { doc, getDoc } from "firebase/firestore";
import {
  authButton,
  backButton,
  bodyContainer,
  cardItemText,
  container,
  divider,
  input,
  itemText,
  modalContainer,
  payButton,
  subContainer,
} from "../Payment/styles";
import { titleText } from "../Login/styles";
import { FirebaseError } from "firebase/app";

const FingerPrintsPayment = () => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalType, setModalType] = useState<string>("");
  const [voucher, setVoucher] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [userId, setUserId] = useState<string>("");
  const [partnerUID, setPartnerUID] = useState<string>("");
  const [fingerprint, setFingerprint] = useState<FingerprintsType>();
  const fingerPrintId = window.location.href.split("/").pop();
  const [user, setUser] = useState<UserType>();

  const navigate = useNavigate();

  useEffect(() => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        let authUser = user as UserType;
        setUser(authUser);
        const uid = authUser.uid;
        if (authUser.partnership) {
          setPartnerUID(authUser.partnership);
        } else {
          setPartnerUID(uid);
        }
        setUserId(uid);
      } else {
        console.log("user is logged out");
      }
    });
  }, []);

  useEffect(() => {
    getInspection();
  }, [userId, fingerPrintId]);

  const getInspection = async () => {
    if (userId && fingerPrintId) {
      const docRef = doc(db, `users/${userId}/fingerprints`, fingerPrintId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const fingerprintData = docSnap.data() as FingerprintsType;
        if (fingerprintData.paid) {
          showToast('Already paid');
          return navigate('/');
        }
        setFingerprint(fingerprintData);
      } else {
        // docSnap.data() will be undefined in this case
        console.log("No such document!");
      }
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const payWithVoucher = () => {
    setIsLoading(true);
    if (isEmpty(voucher)) {
      showToast("You have to input the voucher", "error");
      return;
    }
    const voucherPayment = httpsCallable(
      functions,
      "payForFingerprintsWithCoupon"
    );
    voucherPayment({
      couponId: voucher,
      fingerprintId: fingerPrintId,
      partnerUID: partnerUID,
    })
      .then((result: any) => {
        setVoucher("");
        setIsLoading(false);
        setOpen(false);
        navigate("/");
      })
      .catch((error) => {
        if (error instanceof FirebaseError) {
                  showToast((error as FirebaseError).message, "error");
                } else {
                  showToast("Unknown error", "error");
                }
        setIsLoading(false);
        setVoucher("");
        setOpen(false);
      });
  };

  const getFingerPrintDateString = () => {
    if (!!fingerprint && !!fingerprint.date) {
      const date = new Date(fingerprint.date);
      return date.toISOString().slice(0, 10);
    }
    return new Date().toISOString().slice(0, 10);
  };

  const sendEmailLink = () => {
    setIsLoading(true);
    if (isEmpty(email)) {
      showToast("You have to input the email address", "error");
      return;
    }
    if (!validEmail(email)) {
      showToast("Your email address in invalid", "error");
      return;
    }
    const emailSend = httpsCallable(functions, "emailLinkForPaymentV2");
    emailSend({
      email: email,
      date: fingerprint?.date ? fingerprint?.date : new Date().getTime(),
      name: fingerprint?.name ? fingerprint.name : "",
      surname: fingerprint?.surname ? fingerprint?.surname : "",
      dateString: getFingerPrintDateString(),
      fingerprintId: fingerPrintId,
      partnerUID: partnerUID,
    })
      .then((result: any) => {
        setEmail("");
        setIsLoading(false);
        setOpen(false);
        navigate("/");
      })
      .catch((error) => {
        if (error instanceof FirebaseError) {
          showToast((error as FirebaseError).message, "error");
        } else {
          showToast("Unknown error", "error");
        }
        setVoucher("");
        setIsLoading(false);
        setOpen(false);
      });
  };

  const paywithCard = () => {
    setOpen(true);
    setModalType("card");
    if (fingerprint?.order) {
      setOpen(false);
      navigate(
        `/viewOrder/${fingerprint.order.id}`
      );
    }
    const cardPayment = httpsCallable(functions, "emailLinkForPaymentV2");
    cardPayment({
      email: user?.email || "",
      date: fingerprint?.date || new Date().getTime(),
      name: fingerprint?.name || "",
      surname: fingerprint?.surname || "",
      dateString: getFingerPrintDateString(),
      fingerprintId: fingerPrintId,
      partnerUID: partnerUID,
      onlyCreateOrder: true,
    })
      .then((result: any) => {
        setOpen(false);
        navigate(
          `/viewOrder/${result.data.id}`
        );
      })
      .catch((error) => {
        if (error instanceof FirebaseError) {
          showToast((error as FirebaseError).message, "error");
        } else {
          showToast("Unknown error", "error");
        }
        setOpen(false);
      });
  };

  const payWithVoucherModal = (
    <Box>
      <Typography sx={(theme) => itemText(theme)}>Voucher</Typography>
      <TextField
        placeholder="Voucher"
        size="small"
        fullWidth
        type="text"
        required
        sx={(theme) => input(theme)}
        value={voucher}
        onChange={(e) => setVoucher(e.target.value)}
      />
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          sx={(theme) => payButton(theme)}
          onClick={() => payWithVoucher()}
          disabled={isLoading}
        >
          Pay With Voucher
          {isLoading && <CircularProgress size={20} sx={{ color: "white" }} />}
        </Button>
      </Box>
    </Box>
  );

  const paywithCardModal = (
    <Box>
      <Typography sx={(theme) => cardItemText(theme)}>Pay With Card</Typography>
      <Typography
        sx={{
          color: colors.light_black,
          fontSize: "12px",
          textAlign: "center",
          m: "10px 0",
          fontWeight: 600,
        }}
      >
        Please wait for a while...
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <CircularProgress size={40} />
      </Box>
    </Box>
  );

  const sendEmailLinkModal = (
    <Box>
      <Typography sx={(theme) => itemText(theme)}>
        Email payment link to
      </Typography>
      <TextField
        placeholder="Email Address"
        size="small"
        fullWidth
        type="text"
        required
        sx={(theme) => input(theme)}
        value={email}
        onChange={(e) => setEmail(e.target.value)}
      />
      <Box sx={{ display: "flex", justifyContent: "center" }}>
        <Button
          variant="contained"
          sx={(theme) => payButton(theme)}
          onClick={() => sendEmailLink()}
          disabled={isLoading}
        >
          Send Link
          {isLoading && <CircularProgress size={20} sx={{ color: "white" }} />}
        </Button>
      </Box>
    </Box>
  );

  return (
    <Box sx={(theme) => container(theme)}>
      <Box sx={(theme) => bodyContainer(theme)}>
        <Box sx={(theme) => subContainer(theme)}>
          <Box sx={{ pb: 10 }}>
            <Typography sx={(theme) => titleText(theme)}>
              Choose Payment Option
            </Typography>
            <Divider sx={(theme) => divider(theme)}>pay with</Divider>
            <Button
              fullWidth
              variant="contained"
              sx={(theme) => authButton(theme)}
              onClick={() => {
                paywithCard();
              }}
            >
              Credit / Debit Card
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={(theme) => authButton(theme)}
              onClick={() => {
                setOpen(true);
                setModalType("voucher");
              }}
            >
              RaspberyONE Voucher
            </Button>
            <Button
              fullWidth
              variant="contained"
              sx={(theme) => authButton(theme)}
              onClick={() => {
                setOpen(true);
                setModalType("email");
              }}
            >
              Payment link email
            </Button>
          </Box>
          <Typography
            sx={{
              textAlign: "center",
              fontSize: "12px",
              color: colors.dark_gray,
              mt: 2,
            }}
          >
            By clicking continue, you agree to our{" "}
            <b style={{ color: "black" }}>Terms of Service</b> and{" "}
            <b style={{ color: "black" }}>Privacy Policy</b>
          </Typography>
          <Box sx={{ width: "100%" }}>
            <Button
              variant="contained"
              sx={(theme) => backButton(theme)}
              onClick={() => {
                navigate(-1);
              }}
            >
              Back
            </Button>
          </Box>
        </Box>
      </Box>
      <Modal
        open={open}
        onClose={handleClose}
        aria-labelledby="parent-modal-title"
        aria-describedby="parent-modal-description"
      >
        <Box sx={(theme) => modalContainer(theme)}>
          {modalType === "voucher"
            ? payWithVoucherModal
            : modalType === "card"
            ? paywithCardModal
            : sendEmailLinkModal}
        </Box>
      </Modal>
    </Box>
  );
};

export default FingerPrintsPayment;
